/**
 * Nova context helpers.
 *
 * Every AI request from the app should flow through the `nova-gateway`
 * edge function with a `capability` and a `context` object. The gateway
 * injects context into Nova's system prompt so she never has to ask
 * "which page are you on / what are you looking at?".
 *
 * This file owns:
 *   - the shared Capability type
 *   - a `deriveNovaContext(pathname)` helper that infers what surface
 *     the user is on from the current route
 *   - the `callNovaGateway` streaming transport factory used by the
 *     launcher, Nova Design, and any future surface
 */
import { DefaultChatTransport } from 'ai';
import { novaIntelligenceOn } from '@/hooks/useNovaIntelligence';

/** What a refused request says, so every surface reports it the same. */
export const NOVA_OFF_REASON =
  'Nova Intelligence is off. Turn it back on in Settings → Nova.';

export type NovaCapability =
  | 'code'
  | 'design'
  | 'writing'
  | 'strategy'
  | 'chat'
  | 'default';

export interface NovaSurface {
  /** Human-readable label for the current screen ("CRM", "Calendar", …). */
  surface: string;
  /** What Nova should assume the user cares about right now. */
  focus: string;
  /** Suggested capability bias for prompts from this surface. */
  capability: NovaCapability;
  /** Raw pathname for debugging / prompt inspection. */
  pathname: string;
}

/**
 * Map the current URL to a Nova surface descriptor. Kept as a pure
 * function so it's trivial to unit-test and to extend as new modules
 * are added.
 */
export function deriveNovaContext(pathname: string): NovaSurface {
  const p = pathname.toLowerCase();

  if (p.startsWith('/nova-design'))
    return { surface: 'Nova Design', focus: 'designing and building a new digital asset from scratch', capability: 'design', pathname };
  if (p.startsWith('/sites'))
    return { surface: 'Sites', focus: "the user's website — pages, sections, brand, copy", capability: 'design', pathname };
  if (p.startsWith('/assistant'))
    return { surface: 'Nova Workspace', focus: 'an open-ended conversation with Nova', capability: 'chat', pathname };
  if (p.startsWith('/clients') || p.startsWith('/inquiries'))
    return { surface: 'CRM', focus: 'clients, leads and inquiries', capability: 'strategy', pathname };
  if (p.startsWith('/calendar'))
    return { surface: 'Calendar', focus: 'schedule, upcoming events and deadlines', capability: 'strategy', pathname };
  if (p.startsWith('/projects') || p.startsWith('/studio'))
    return { surface: 'Projects', focus: 'the active project, its team and milestones', capability: 'strategy', pathname };
  if (p.startsWith('/billing') || p.startsWith('/expenses'))
    return { surface: 'Billing', focus: 'invoices, estimates and cash flow', capability: 'strategy', pathname };
  if (p.startsWith('/deliveries') || p.startsWith('/galleries'))
    return { surface: 'Deliveries', focus: 'final deliverables and client galleries', capability: 'writing', pathname };
  if (p.startsWith('/settings'))
    return { surface: 'Settings', focus: 'account, brand and workspace configuration', capability: 'strategy', pathname };
  if (p === '/' || p === '')
    return { surface: 'Dashboard', focus: "the business's top-level snapshot", capability: 'strategy', pathname };

  return { surface: 'Orbit', focus: 'general assistance', capability: 'default', pathname };
}

/**
 * Build a `DefaultChatTransport` pointed at the Nova gateway. Every
 * caller passes its own `capability`/`context` provider so a single
 * transport instance can adapt to route changes without being rebuilt.
 */
export function createNovaTransport(opts: {
  authToken: () => string | null;
  capability?: () => NovaCapability;
  context?: () => unknown;
  /** Opt-in persistence key. Pass a stable slug (e.g. "launcher")
   *  and the gateway will persist this conversation to nova_threads/
   *  nova_messages under (user_id, surface). Omit for ephemeral. */
  threadSurface?: () => string | null;
}) {
  return new DefaultChatTransport({
    api: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nova-gateway`,
    // NOVA-OFF-2 — the guard belongs HERE. NOVA-OFF-1 put it in
    // NovaLauncher's `send` and said it "sits on the one path every
    // message takes" — it didn't. /assistant, GoogieNova and design
    // mode each build their own transport and each kept answering with
    // the switch off. They all come through this factory, so refusing
    // at the fetch is the one guard no caller can forget.
    fetch: (input, init) => {
      if (!novaIntelligenceOn()) return Promise.reject(new Error(NOVA_OFF_REASON));
      return globalThis.fetch(input, init);
    },
    headers: () => ({
      Authorization: `Bearer ${opts.authToken() ?? ''}`,
      'Content-Type': 'application/json',
    }),
    body: () => {
      const surface = opts.threadSurface?.() ?? null;
      return {
        capability: opts.capability?.() ?? 'default',
        context: opts.context?.() ?? null,
        ...(surface ? { thread_surface: surface } : {}),
      };
    },
  });
}
